/**
 * Rosie -- NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
// On-time check-ins (Paula 2026-10-21): LGH tracks our on-time check-ins on
// their side. This page is OUR side, from the driver taps -- en route, at
// comms, at bedside, picked up, dropped off, complete -- for every LGH pickup,
// with a CSV download to line up against LGH's numbers. No patient names.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { todayET } from '../utils/et';

const addDays = (d, n) => { const t = new Date(d + 'T12:00:00Z'); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };
const mondayOf = (d) => { const w = new Date(d + 'T12:00:00Z').getUTCDay(); return addDays(d, w === 0 ? -6 : 1 - w); };
const tm = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '';
const dt = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'America/New_York' }) : '';
const signed = (m) => m == null ? '' : (m > 0 ? `+${m}` : `${m}`);

const COLS = [
  ['Trip', t => (t.transport_number || '').replace('TRP-2026-', '') + (t.is_return ? ' (ret)' : '')],
  ['Date', t => dt(t.scheduled_time)],
  ['Sched', t => tm(t.scheduled_time)],
  ['Crew', t => t.crew],
  ['En route', t => tm(t.en_route_time)],
  ['At comms', t => tm(t.at_comms_at)],
  ['Comms vs sched (min)', t => signed(t.comms_vs_scheduled_min)],
  ['At bedside', t => tm(t.bedside_at)],
  ['Bedside vs sched (min)', t => signed(t.bedside_vs_scheduled_min)],
  ['Picked up', t => tm(t.picked_up_at)],
  ['Bedside wait (min)', t => t.bedside_wait_min == null ? '' : String(t.bedside_wait_min)],
  ['Dropped off', t => tm(t.dropped_off_at)],
  ['Complete', t => tm(t.completed_at)],
  ['Wait reason', t => t.readiness_note],
];

function downloadCsv(data) {
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const lines = [COLS.map(c => esc(c[0])).join(',')]
    .concat(data.trips.map(t => COLS.map(c => esc(c[1](t))).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `lgh-checkins-${data.start}-to-${data.end}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Green at/above LGH's target (80%), amber within 10 points, red below.
const band = (v, target) => v == null ? '#999' : (v >= target ? '#0F6E56' : (v >= target - 10 ? '#B26B00' : '#A32D2D'));

function MetricPanel({ title, big, target, sub, rows }) {
  return (
    <div style={{ background: '#f5f5f3', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{title}</div>
        <div style={{ fontSize: 11, color: '#999' }}>LGH target {target}%+</div>
      </div>
      <div style={{ fontSize: 34, fontWeight: 600, color: band(big, target), lineHeight: 1.2, marginTop: 4 }}>{big == null ? '--' : big + '%'}</div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>{sub}</div>
      {rows.map(([label, value, warn]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '3px 0', borderTop: '0.5px solid #e5e5e5' }}>
          <span style={{ color: '#666' }}>{label}</span>
          <span style={{ fontWeight: 500, color: warn ? '#A32D2D' : '#333', textAlign: 'right' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

function BreakdownTable({ title, keyName, label, rows, target, fmt }) {
  const cell = { padding: '5px 8px', textAlign: 'right' };
  return (
    <div style={{ border: '0.5px solid #e5e5e5', borderRadius: 10, overflowX: 'auto' }}>
      <div style={{ fontSize: 13, fontWeight: 600, padding: '8px 10px', background: '#fafaf8' }}>{title}</div>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', whiteSpace: 'nowrap' }}>
        <thead>
          <tr style={{ color: '#666' }}>
            <th style={{ ...cell, textAlign: 'left', fontWeight: 500 }}>{label}</th>
            <th style={{ ...cell, fontWeight: 500 }}>Trips</th>
            <th style={{ ...cell, fontWeight: 500 }}>On time at comms</th>
            <th style={{ ...cell, fontWeight: 500 }}>On time at bedside</th>
            <th style={{ ...cell, fontWeight: 500 }}>Missed comms / bedside</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r[keyName]} style={{ borderTop: '0.5px solid #eee' }}>
              <td style={{ ...cell, textAlign: 'left' }}>{fmt ? fmt(r[keyName]) : r[keyName]}</td>
              <td style={cell}>{r.trips}</td>
              <td style={{ ...cell, fontWeight: 600, color: band(r.on_time_comms_pct, target) }}>
                {r.on_time_comms_pct == null ? '--' : r.on_time_comms_pct + '%'}
                <span style={{ fontWeight: 400, color: '#999' }}> ({r.on_time_comms}/{r.comms_tapped})</span>
              </td>
              <td style={{ ...cell, fontWeight: 600, color: band(r.on_time_bedside_pct, target) }}>
                {r.on_time_bedside_pct == null ? '--' : r.on_time_bedside_pct + '%'}
                <span style={{ fontWeight: 400, color: '#999' }}> ({r.on_time_bedside}/{r.bedside_tapped})</span>
              </td>
              <td style={{ ...cell, color: (r.trips - r.comms_tapped) + (r.trips - r.bedside_tapped) > 0 ? '#A32D2D' : '#333' }}>{r.trips - r.comms_tapped} / {r.trips - r.bedside_tapped}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} style={{ padding: 12, color: '#999', textAlign: 'center' }}>No completed pickups.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// Who has read and initialed the required driver guides (Paula 2026-10-28).
function GuideSignoffs() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/drivers/guides/signoffs').then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.guides.length) return null;
  const signed = (g) => data.drivers.filter(d => d.signoffs[g.key]).length;
  return (
    <div style={{ border: '0.5px solid #e5e5e5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Driver guide sign-offs</div>
      <div style={{ fontSize: 12, color: '#999', marginTop: 2, marginBottom: 8 }}>
        Drivers get a pop-up in their app until they read the guide and initial it.
        {data.guides.map(g => ` ${g.title}: ${signed(g)} of ${data.drivers.length} signed.`).join('')}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ background: '#fafaf8' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: '#666' }}>Driver</th>
              {data.guides.map(g => <th key={g.key} style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: '#666' }}>{g.title}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.drivers.map(d => (
              <tr key={d.id} style={{ borderTop: '0.5px solid #eee' }}>
                <td style={{ padding: '6px 8px' }}>{d.name}</td>
                {data.guides.map(g => {
                  const s = d.signoffs[g.key];
                  return (
                    <td key={g.key} style={{ padding: '6px 8px', ...(s ? { color: '#1D7A5C' } : { background: '#FCEBEB', color: '#A32D2D' }) }}>
                      {s ? `${s.initials} on ${new Date(s.at).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}`
                        + (s.due ? ` (re-sign by ${new Date(s.due).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: 'America/New_York' })})` : '')
                        : (g.resign_days ? 'Not yet, or expired' : 'Not yet')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CheckIns() {
  const [start, setStart] = useState(mondayOf(todayET()));
  const [end, setEnd] = useState(addDays(mondayOf(todayET()), 6));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/transports/checkin-report', { params: { start, end } });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load check-ins');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (n) => { setStart(addDays(start, 7 * n)); setEnd(addDays(end, 7 * n)); };
  const s = data?.summary;
  const missing = (v) => v ? {} : { background: '#FCEBEB', color: '#A32D2D' };

  return (
    <div style={{ padding: '16px 0' }}>
      <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>LGH check-ins</h1>
      <div style={{ fontSize: 12, color: '#999', marginTop: 2, marginBottom: 12 }}>
        Our driver taps on every LGH pickup -- to compare with LGH's on-time numbers. Red = tap missed.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => shiftWeek(-1)} style={{ fontSize: 12, padding: '6px 10px' }}>&larr; Prev week</button>
        <input type="date" value={start} onChange={e => setStart(e.target.value)} style={{ fontSize: 12, padding: 4 }} />
        <span style={{ fontSize: 12, color: '#999' }}>to</span>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={{ fontSize: 12, padding: 4 }} />
        <button onClick={() => shiftWeek(1)} style={{ fontSize: 12, padding: '6px 10px' }}>Next week &rarr;</button>
        <button onClick={() => data && downloadCsv(data)} disabled={!data || !data.trips.length}
          style={{ fontSize: 12, padding: '6px 12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 6 }}>
          Download CSV
        </button>
      </div>

      {error && <div style={{ color: '#A32D2D', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {loading && !data && <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>}

      {s && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 10, marginBottom: 12 }}>
            <MetricPanel
              title="On time at comms"
              big={s.on_time_comms_pct}
              target={s.target_pct}
              sub={`${s.on_time_comms} of ${s.comms_tapped} check-ins were at or before the scheduled time`}
              rows={[
                ['Missed (no at-comms tap, not counted)', `${s.completed - s.comms_tapped} of ${s.completed}`, s.completed - s.comms_tapped > 0],
                ['Late check-ins', s.late_comms ? `${s.late_comms} (avg ${s.avg_late_comms_min} min late, ${s.late_comms_within_5} within 5 min)` : '0'],
              ]}
            />
            <MetricPanel
              title="On time at bedside"
              big={s.on_time_bedside_pct}
              target={s.target_pct}
              sub={`${s.on_time_bedside} of ${s.bedside_tapped} were at bedside at or before the scheduled time`}
              rows={[
                ['Missed (no at-bedside tap, not counted)', `${s.completed - s.bedside_tapped} of ${s.completed}`, s.completed - s.bedside_tapped > 0],
                ['When late, avg', s.avg_late_bedside_min == null ? '--' : s.avg_late_bedside_min + ' min after sched'],
                ['Comms to bedside (median)', s.median_comms_to_bedside_min == null ? '--' : s.median_comms_to_bedside_min + ' min'],
                ['Avg bedside wait for patient', s.avg_bedside_wait_min == null ? '--' : s.avg_bedside_wait_min + ' min'],
                ['10+ min waits with a reason', `${s.long_waits_with_reason} of ${s.long_waits}`, s.long_waits_with_reason < s.long_waits],
              ]}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 10, marginBottom: 16 }}>
            <BreakdownTable title="By driver" keyName="name" label="Driver" rows={data.by_driver || []} target={s.target_pct} />
            <BreakdownTable title="By day" keyName="day" label="Day" rows={data.by_day || []} target={s.target_pct}
              fmt={(d) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC' })} />
          </div>
        </>
      )}

      <GuideSignoffs />

      {data && (
        <div style={{ overflowX: 'auto', border: '0.5px solid #e5e5e5', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', whiteSpace: 'nowrap' }}>
            <thead>
              <tr style={{ background: '#fafaf8' }}>
                {COLS.map(c => <th key={c[0]} style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500, color: '#666' }}>{c[0]}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.trips.map(t => {
                const live = t.status !== 'completed';
                return (
                  <tr key={t.id} style={{ borderTop: '0.5px solid #eee' }}>
                    {COLS.map(([label, fn]) => {
                      const v = fn(t);
                      const tap = ['En route', 'At comms', 'At bedside', 'Picked up', 'Dropped off', 'Complete'].includes(label);
                      const late = label === 'Comms vs sched (min)' && t.comms_vs_scheduled_min > 0;
                      const style = { padding: '6px 8px', ...(tap && !live ? missing(v) : {}), ...(late ? { color: '#A32D2D', fontWeight: 600 } : {}) };
                      if (label === 'Wait reason') style.whiteSpace = 'normal';
                      return <td key={label} style={style}>{v || (tap && !live ? 'missed' : '')}</td>;
                    })}
                  </tr>
                );
              })}
              {!data.trips.length && (
                <tr><td colSpan={COLS.length} style={{ padding: 16, color: '#999', textAlign: 'center' }}>No LGH pickups in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
